import { formatPhoneInput, maskNpiInput, stripPhone } from '@/shared/lib/formatters';
import { commitOrPushErrors, getFieldState } from '@/shared/lib/forms';
import { FormField, StateMultiSelect } from '@/shared/ui/primitives';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { FieldGroup } from '@/shared/ui/shadcn/field';

import { useInitialScreeningForm } from '../../model/useInitialScreeningForm';
import type { InitialScreeningPayload } from '../../model/useClinicianOnboardingOrchestrator';
import { DS_STEP_FORM_ID } from './_shared/dsStepFormId';

interface InitialScreeningStepProps {
  onSubmit: (payload: InitialScreeningPayload) => void;
  isSubmitting: boolean;
  submitError: Error | null;
}

export function InitialScreeningStep({
  onSubmit,
  isSubmitting,
  submitError,
}: InitialScreeningStepProps) {
  const { form, validateDraft } = useInitialScreeningForm();

  function handleSubmit() {
    const data = commitOrPushErrors(form, validateDraft());
    if (!data) {
      return;
    }
    onSubmit({
      data: {
        ...data,
        phone_number: data.phone_number ? stripPhone(data.phone_number) : data.phone_number,
      },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      {submitError ? (
        <Alert variant="destructive">
          <AlertDescription>{submitError.message}</AlertDescription>
        </Alert>
      ) : null}

      <form
        id={DS_STEP_FORM_ID}
        onSubmit={(e) => {
          e.preventDefault();
          if (!isSubmitting) {
            handleSubmit();
          }
        }}
      >
        <FieldGroup>
          <FormField
            form={form}
            type="text"
            name="display_name"
            label="Full name"
            placeholder="Jane Smith"
            inputProps={{ autoComplete: 'name', autoFocus: true }}
          />

          <FormField
            form={form}
            type="text"
            name="email"
            label="Email"
            placeholder="jane@example.com"
            inputProps={{ autoComplete: 'email' }}
          />

          <FormField
            form={form}
            type="text"
            name="phone_number"
            label="Phone number"
            placeholder="(555) 123-4567"
            parse={formatPhoneInput}
            inputProps={{ autoComplete: 'tel', inputMode: 'tel' }}
          />

          <FormField
            form={form}
            type="text"
            name="medical_school"
            label="Medical school"
            placeholder="School name"
          />

          <FormField
            form={form}
            type="text"
            name="degree"
            label="Degree"
            placeholder="MD, DO, etc."
          />

          <FormField
            form={form}
            type="text"
            name="residency"
            label="Residency"
            placeholder="Residency program"
          />

          <FormField
            form={form}
            type="text"
            name="fellowships"
            label="Fellowships"
            placeholder="Fellowship (if any)"
          />

          <FormField
            form={form}
            type="text"
            name="specialty"
            label="Specialty"
            placeholder="Primary specialty"
          />

          <FormField
            form={form}
            type="text"
            name="npi_number"
            label="NPI number"
            placeholder="10-digit NPI"
            parse={maskNpiInput}
            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
          />

          <form.Field
            name="licensed_states"
            children={(field) => {
              const selected: string[] = (field.state.value as string[] | null | undefined) ?? [];
              const { isInvalid, errors } = getFieldState(field.state.meta);
              return (
                <StateMultiSelect
                  value={selected}
                  onChange={(codes) =>
                    (field.handleChange as unknown as (v: string[]) => void)(codes)
                  }
                  disabled={isSubmitting}
                  error={
                    isInvalid ? (errors[0]?.message ?? 'At least one state is required') : null
                  }
                />
              );
            }}
          />

          <FormField
            form={form}
            type="text"
            name="referral_source"
            label="How did you hear about us?"
            placeholder="e.g. colleague, conference, online"
          />

          <FormField
            form={form}
            type="text"
            name="referral_person_name"
            label="Referred by"
            placeholder="Name of referring person (if any)"
          />

          <FormField
            form={form}
            type="checkbox"
            name="board_certified"
            label="I am board certified"
          />
        </FieldGroup>
      </form>
    </div>
  );
}
